/* eslint-disable react/prop-types */
import React, {
  Suspense,
  lazy,
  useEffect,
  useState,
  memo,
  useRef,
} from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./index.css";
import "./App.css";
import {
  Header,
  Footer,
  SideNavCustom,
  SupportingPatternTable,
} from "./shared";
import Login from "./pages/Login";
import LoggedOut from "./pages/LoggedOut";
import { CloudProviderProvider } from "./context/CloudProviderContext";
import axios from "axios";
import { SafIcon } from "@saffron/core-components/react";
import { useSession } from "./util/useSession";
import {
  trackLogin,
  trackPageView,
  clearAuthenticatedUser,
} from "./util/appInsights";

const Home = lazy(() => import("./pages/Home"));
const Groups = lazy(() => import("./pages/Groups"));
const AssetInsight = lazy(() =>
  import("./features/tech-stack/components/Assetinsight")
);
const Summary = lazy(() => import("./pages/Summary"));
const ArchitectureDetails = lazy(() =>
  import("./features/tech-stack/components/Architecturedetails")
);
const TechnologyStacks = lazy(() =>
  import("./features/tech-stack/pages/TechnologyStacksPage")
);
const CloudPermit = lazy(() =>
  import("./features/cloud-permit/pages/CloudPermitPage")
);
const Addruntime = lazy(() =>
  import("./features/tech-stack/components/Addruntime")
);
const Customruntime = lazy(() =>
  import("./features/tech-stack/components/Customruntime")
);
const Addtechstack = lazy(() =>
  import("./features/tech-stack/components/Addtechstack")
);
const Addbackendtechstack = lazy(() =>
  import("./features/tech-stack/components/Addbackendtechstack")
);
const Adddevopstechstack = lazy(() =>
  import("./features/tech-stack/components/Adddevopstechstack")
);

// Memoized Summary component
const MemoizedSummary = memo(function MemoizedSummary() {
  const [localContainerForm, setLocalContainerForm] = useState({
    additional_contacts: [],
    Title: "",
    Name: "",
  });
  return (
    <Suspense fallback={<div>Loading Summary...</div>}>
      <Summary
        containerForm={localContainerForm}
        setContainerForm={setLocalContainerForm}
      />
    </Suspense>
  );
});

// Enhanced HomeRouteHandler with better state management
function HomeRouteHandler({ saveUserData, containerForm, userData }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [authenticationComplete, setAuthenticationComplete] = useState(false);
  // Guard to prevent double-processing (e.g., React.StrictMode double effect invocation)
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    if (process.env.REACT_APP_IS_ENV_LOCALHOST) {
      if (userData) {
        setAuthenticationComplete(true);
      }
      return;
    }

    const query = new URLSearchParams(location.search);
    const code = query.get("code");
    const state = query.get("state");
    const error = query.get("error");
    const bypassMfaParam = query.get("bypassmfacaptcha");

    // Check if bypassmfacaptcha is in URL and store it
    if (bypassMfaParam === "true") {
      sessionStorage.setItem("bypassmfacaptcha", "true");
    }

    // Handle OAuth errors first
    if (error) {
      console.error("OAuth Error:", error);
      sessionStorage.removeItem("oauthState");
      sessionStorage.removeItem("originalPath");
      sessionStorage.removeItem("bypassmfacaptcha");
      sessionStorage.removeItem("lastSSORedirect");
      navigate("/login", { replace: true });
      return;
    }

    // Check if we have a valid OAuth callback
    if (code && !authenticationComplete && !hasProcessedRef.current) {
      const storedState = sessionStorage.getItem("oauthState");

      // Validate state parameter
      if (!state || !storedState || state !== storedState) {
        console.error("Invalid OAuth state parameter", {
          received: state,
          stored: storedState,
          hasReceived: !!state,
          hasStored: !!storedState,
        });
        // Clear OAuth data and redirect to login
        sessionStorage.removeItem("oauthState");
        sessionStorage.removeItem("originalPath");
        sessionStorage.removeItem("bypassmfacaptcha");
        sessionStorage.removeItem("lastSSORedirect");
        navigate("/login", { replace: true });
        return;
      }
      // Process the OAuth code (guard further runs)
      hasProcessedRef.current = true;
      setProcessing(true);

      const processOAuthCode = async () => {
        try {
          // Check if MFA bypass is enabled (currently not used but kept for reference)
          // Defer clearing oauthState until after successful token exchange to avoid race conditions
          const tokenUrl = "https://sso.thomsonreuters.com/as/token.oauth2";
          const tokenData = new URLSearchParams({
            grant_type: "authorization_code",
            client_id: process.env.REACT_APP_CLIENT_ID,
            client_secret: process.env.REACT_APP_CLIENT_SECRET,
            code: code,
            redirect_uri: process.env.REACT_APP_REDIRECT_URI,
          });

          const tokenResponse = await axios.post(tokenUrl, tokenData, {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          });

          if (tokenResponse.data && tokenResponse.data.access_token) {
            const profileUrl =
              "https://sso.thomsonreuters.com/idp/userinfo.openid";

            const profileData = new URLSearchParams({
              schema: "openid",
              scope: "openid email profile",
              access_token: tokenResponse.data.access_token,
            });

            const profileResponse = await axios.post(profileUrl, profileData, {
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
              },
            });

            if (profileResponse.data) {
              const newUserData = {
                id: profileResponse.data.sub,
                email: profileResponse.data.mail,
                firstName:
                  profileResponse.data.firstname ||
                  profileResponse.data.legalname,
                lastName:
                  profileResponse.data.lastname || profileResponse.data.sn,
                fullName: profileResponse.data.cn,
              };

              // Store user data
              saveUserData(newUserData);
              setAuthenticationComplete(true);

              // Tag this browser session with the SSO identity and emit a
              // one-off UserLogin event to Application Insights. All future
              // page views from this user will be linked to this identity.
              trackLogin(newUserData);

              // Clear state now that flow succeeded
              sessionStorage.removeItem("oauthState");

              // Get original path and clean up
              const originalPath = sessionStorage.getItem("originalPath");
              sessionStorage.removeItem("originalPath");

              // Clear bypassmfacaptcha flag after successful authentication
              const wasBypassMfa =
                sessionStorage.getItem("bypassmfacaptcha") === "true";
              sessionStorage.removeItem("bypassmfacaptcha");
              if (wasBypassMfa) {
                console.log(
                  "MFA bypass was successful - user authenticated without MFA"
                );
              }
              // Clean up the URL by removing OAuth parameters
              const cleanUrl =
                window.location.origin + window.location.pathname;
              window.history.replaceState({}, document.title, cleanUrl);

              // Navigate to original path or home
              if (
                originalPath &&
                originalPath !== "/home" &&
                originalPath !== "/"
              ) {
                // Use setTimeout to ensure state updates are processed
                setTimeout(() => {
                  navigate(originalPath, { replace: true });
                }, 100);
              } else {
                // Stay on home page but force re-render
                setTimeout(() => {
                  navigate("/home", { replace: true });
                }, 100);
              }
            } else {
              throw new Error("Failed to get user profile");
            }
          } else {
            throw new Error("Failed to get access token");
          }
        } catch (error) {
          console.error("OAuth processing error:", error);
          sessionStorage.removeItem("oauthState");
          sessionStorage.removeItem("originalPath");
          sessionStorage.removeItem("bypassmfacaptcha");
          sessionStorage.removeItem("lastSSORedirect");
          navigate("/login", { replace: true });
        } finally {
          setProcessing(false);
        }
      };
      processOAuthCode();
    }
    // If no code but also no user data, redirect to login
    else if (!code && !authenticationComplete) {
      const hasOAuthState = sessionStorage.getItem("oauthState");
      const lastRedirect = sessionStorage.getItem("lastSSORedirect");

      if (lastRedirect && Date.now() - parseInt(lastRedirect) < 5000) {
        console.log("Recent redirect detected in HomeRouteHandler, waiting...");
        return;
      }
      // Only redirect to login if:
      // 1. No stored user data exists
      // 2. We're not in the middle of an OAuth flow (no oauthState)
      if (!userData && !hasOAuthState) {
        const fullPath = location.pathname + location.search;
        // Preserve bypassmfacaptcha parameter if present in URL
        const urlParams = new URLSearchParams(location.search);
        const bypassMfaParam = urlParams.get("bypassmfacaptcha");
        if (bypassMfaParam === "true") {
          sessionStorage.setItem("bypassmfacaptcha", "true");
          console.log(
            "Preserving bypassmfacaptcha parameter before redirect to login"
          );
        }
        sessionStorage.setItem("originalPath", fullPath);
        navigate("/login", { replace: true });
      } else if (hasOAuthState) {
        console.log(
          "OAuth flow in progress - waiting for authentication to complete"
        );
      }
    }
  }, [
    location.search,
    location.pathname,
    navigate,
    saveUserData,
    processing,
    authenticationComplete,
    userData,
  ]);

  // Reset authentication state when leaving the page
  useEffect(() => {
    return () => {
      if (!location.search.includes("code=")) {
        setAuthenticationComplete(false);
      }
    };
  }, [location.search]);

  if (processing) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <div>Processing authentication...</div>
        <div style={{ marginTop: "10px", fontSize: "14px", color: "#666" }}>
          Please wait while we complete your login.
        </div>
      </div>
    );
  }
  // Render Home component if we have user data
  if (
    userData &&
    (authenticationComplete || !location.search.includes("code="))
  ) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Home
          userData={userData}
          setUserData={() => {}}
          additional_contacts={containerForm?.additional_contacts || []}
        />
      </Suspense>
    );
  }
  return (
    <div style={{ padding: "20px", textAlign: "center" }}>
      Checking authentication...
    </div>
  );
}

function App() {
  const { userData, saveUserData, logout } = useSession();
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [containerForm, setContainerForm] = useState({
    additional_contacts: [],
    Title: "",
    Name: "",
  });

  useEffect(() => {
    if (process.env.REACT_APP_IS_ENV_LOCALHOST && !userData) {
      try {
        const localSeed = JSON.parse(process.env.REACT_APP_USER_DATA);
        saveUserData(localSeed);
        // Also tag App Insights with the local seeded identity so developer
        // telemetry (if the connection string is configured) is attributed.
        trackLogin(localSeed);
        console.log("Local environment: seeded userData with expiry");
      } catch (e) {
        console.error("Invalid REACT_APP_USER_DATA JSON:", e);
      }
    }
  }, [userData, saveUserData]);

  const handleLogout = () => {
    // Clear the authenticated-user tag in App Insights so the next user on
    // this browser doesn't inherit the previous user's identity in telemetry.
    clearAuthenticatedUser();
    logout();
    setIsLoggedOut(true);
  };

  const ProtectedRoute = ({ children }) => {
    if (!userData) {
      const fullPath = window.location.pathname + window.location.search;
      sessionStorage.setItem("originalPath", fullPath);
      return <Navigate to="/login" replace />;
    }
    return children;
  };

  return (
    <Router>
      <AppContent
        userData={userData}
        setUserData={() => {}}
        isLoggedOut={isLoggedOut}
        handleLogout={handleLogout}
        containerForm={containerForm}
        setContainerForm={setContainerForm}
        ProtectedRoute={ProtectedRoute}
        saveUserData={saveUserData}
      />
    </Router>
  );
}

function AppContent({
  userData,
  setUserData,
  isLoggedOut,
  handleLogout,
  containerForm,
  ProtectedRoute,
  saveUserData,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPath = location.pathname === "/login";
  const isLoggedOutPath = location.pathname === "/logged-out";
  const [sideNavVisible, setSideNavVisible] = useState(true);

  useEffect(() => {
    document.body.classList.toggle("sidebar-hidden", !sideNavVisible);
  }, [sideNavVisible]);

  
  // Application Insights: track every SPA route change as a page view.
  useEffect(() => {
    if (!userData) return;
    if (isLoginPath || isLoggedOutPath) return;
    trackPageView(location.pathname);
  }, [location.pathname, userData, isLoginPath, isLoggedOutPath]);

  useEffect(() => {
    const onExpired = () =>
      navigate("/logged-out?reason=expired", { replace: true });
    const onLogout = () => navigate("/logged-out", { replace: true });
    window.addEventListener("session-expired", onExpired);
    window.addEventListener("session-logout", onLogout);
    return () => {
      window.removeEventListener("session-expired", onExpired);
      window.removeEventListener("session-logout", onLogout);
    };
  }, [navigate]);

  return (
    <>
      {!isLoginPath && !isLoggedOutPath && userData && !isLoggedOut && (
        <Header
          userData={userData}
          setUserData={setUserData}
          handleLogout={handleLogout}
        />
      )}
      {/* Move SideNavCustom outside of main-content for fixed positioning */}
      {!isLoginPath && !isLoggedOutPath && userData && !isLoggedOut && (
        <SideNavCustom className={sideNavVisible ? "" : "hidden"} />
      )}
      <div className="main-content">
        <div className={`content${sideNavVisible ? "" : " full-width"}`}>
          <button
            className={`toggle-btn${sideNavVisible ? "" : " sidebar-hidden"}`}
            onClick={() => setSideNavVisible((v) => !v)}
            title={sideNavVisible ? "Hide Sidebar" : "Show Sidebar"}
          >
            <span className={`arrow-icon ${sideNavVisible ? "left" : "right"}`}>
              {sideNavVisible ? (
                <SafIcon
                  iconName="chevron-double-left"
                  title="saf-icon"
                ></SafIcon>
              ) : (
                <SafIcon
                  iconName="chevron-double-right"
                  title="saf-icon"
                ></SafIcon>
              )}
            </span>
          </button>

          <Suspense fallback={<div>Loading...</div>}>
            <Routes>
              <Route path="/" element={<Navigate to="/home" />} />
              <Route
                path="/login"
                element={
                  userData ? (
                    <Navigate
                      to={sessionStorage.getItem("originalPath") || "/home"}
                      replace
                    />
                  ) : (
                    <Login />
                  )
                }
              />
              <Route path="/logged-out" element={<LoggedOut />} />
              {/* Home route with enhanced handling */}
              <Route
                path="/home"
                element={
                  <HomeRouteHandler
                    saveUserData={saveUserData}
                    containerForm={containerForm}
                    userData={userData}
                  />
                }
              />
              {/* All other protected routes */}
              <Route
                path="/customruntime"
                element={
                  <ProtectedRoute>
                    <Customruntime />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/groups"
                element={
                  <ProtectedRoute>
                    <Groups />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/asset-insight"
                element={
                  <ProtectedRoute>
                    <AssetInsight />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:assetId/summary"
                element={
                  <ProtectedRoute>
                    <MemoizedSummary />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/runtime"
                element={
                  <ProtectedRoute>
                    <Addruntime />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:assetId/architecture-details"
                element={
                  <ProtectedRoute>
                    <ArchitectureDetails />
                  </ProtectedRoute>
                }
              />
              <Route path="/:assetId/technology-stacks">
                <Route
                  index
                  element={
                    <ProtectedRoute>
                      <TechnologyStacks />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="addtechstack"
                  element={
                    <ProtectedRoute>
                      <Addtechstack />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="addbackendtechstack"
                  element={
                    <ProtectedRoute>
                      <Addbackendtechstack />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="adddevopstechstack"
                  element={
                    <ProtectedRoute>
                      <Adddevopstechstack />
                    </ProtectedRoute>
                  }
                />
              </Route>
              <Route
                path="/:assetId/cloud-permit/:provider?"
                element={
                  <ProtectedRoute>
                    <CloudProviderProvider>
                      <CloudPermit />
                    </CloudProviderProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/supporting-pattern"
                element={
                  <ProtectedRoute>
                    <SupportingPatternTable />
                  </ProtectedRoute>
                }
              />
              <Route
                path="*"
                element={
                  <ProtectedRoute>
                    <Navigate to="/home" />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
        </div>
      </div>
      {!isLoginPath && !isLoggedOutPath && userData && !isLoggedOut && (
        <Footer fullWidth={!sideNavVisible} />
      )}
    </>
  );
}
export default App;
